import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  InputBase,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import PaymentsIcon from '@mui/icons-material/Payments';
import { CurrencyExchange, Storefront } from '@mui/icons-material';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import Person4Icon from '@mui/icons-material/Person4';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';

const searchItems = [
  { title: 'Dashboard', href: '/dashboard/main', icon: DashboardIcon, category: 'Pages' },
  { title: 'Clients', href: '/dashboard/clients', icon: BusinessIcon, category: 'Pages' },
  { title: 'Contracts', href: '/dashboard/contracts', icon: ArticleIcon, category: 'Pages' },
  { title: 'Transactions', href: '/dashboard/transactions', icon: SwapHorizIcon, category: 'Pages' },
  { title: 'Invoices', href: '/dashboard/business-invoices', icon: ReceiptLongIcon, category: 'Pages' },
  { title: 'Follow Ups', href: '/dashboard/follow-ups', icon: FactCheckOutlinedIcon, category: 'Pages' },
  { title: 'Bills', href: '/dashboard/bills', icon: ReceiptIcon, category: 'Pages' },
  { title: 'Ledger', href: '/dashboard/ledger', icon: MenuBookIcon, category: 'Pages' },
  { title: 'Pay Requests', href: '/dashboard/pay-request', icon: PaymentsIcon, category: 'Pages' },
  { title: 'Payment Profiles', href: '/dashboard/masterdata/paymentprofile', icon: AccountBalanceWalletIcon, category: 'Pages' },
  { title: 'Notes', href: '/dashboard/client-notes', icon: DescriptionIcon, category: 'Pages' },
  { title: 'Accounting Tasks', href: '/dashboard/accounting-tasks', icon: FactCheckOutlinedIcon, category: 'Pages' },
  { title: 'Credit Notes', href: '/dashboard/credit-notes', icon: AssignmentReturnIcon, category: 'Pages' },
  { title: 'Work Orders', href: '/dashboard/work-orders', icon: AssignmentIcon, category: 'Pages' },
  { title: 'Reimbursement', href: '/dashboard/reimbursement', icon: CurrencyExchange, category: 'Pages' },
  { title: 'Vendors', href: '/dashboard/vendors', icon: Storefront, category: 'Pages' },
  { title: 'Purchase Orders', href: '/dashboard/purchase-orders', icon: ShoppingCartIcon, category: 'Pages' },
  { title: 'Subscriptions', href: '/dashboard/subscriptions', icon: SubscriptionsIcon, category: 'Pages' },
  { title: 'Employees', href: '/dashboard/employees', icon: GroupsIcon, category: 'Pages' },
  { title: 'Users', href: '/dashboard/users', icon: Person4Icon, category: 'Pages' },
  // Tax
  { title: 'Tax Returns', href: '/dashboard/tax-returns', icon: DescriptionIcon, category: 'Tax' },
  { title: 'Tax Returns Invoices', href: '/dashboard/tax-returns/invoices', icon: ReceiptLongIcon, category: 'Tax' },
  { title: 'TDS', href: '/dashboard/tax/tds', icon: PaymentsIcon, category: 'Tax' },
  // Master Data
  { title: 'Organizations', href: '/dashboard/masterdata/organization', icon: BusinessIcon, category: 'Master Data' },
  { title: 'Tax Accounts', href: '/dashboard/masterdata/taxaccounts', icon: PaymentsIcon, category: 'Master Data' },
  { title: 'Payment Channels', href: '/dashboard/masterdata/paymentchannels', icon: AccountBalanceIcon, category: 'Master Data' },
  { title: 'Tax Scheme', href: '/dashboard/masterdata/taxscheme', icon: DescriptionIcon, category: 'Master Data' },
  { title: 'Tax Codes', href: '/dashboard/masterdata/taxcode', icon: FactCheckOutlinedIcon, category: 'Master Data' },
  { title: 'Countries', href: '/dashboard/masterdata/country', icon: BusinessIcon, category: 'Master Data' },
  { title: 'Currencies', href: '/dashboard/masterdata/currencies', icon: PaymentsIcon, category: 'Master Data' },
  { title: 'Settings', href: '/dashboard/masterdata/settings', icon: SettingsIcon, category: 'Master Data' },
  { title: 'Transaction Categories', href: '/dashboard/masterdata/transaction-category', icon: PriceChangeIcon, category: 'Master Data' },
  { title: 'Vendor Categories', href: '/dashboard/masterdata/vendor-category', icon: ReceiptOutlinedIcon, category: 'Master Data' },
  { title: 'Inventory Categories', href: '/dashboard/masterdata/category', icon: DescriptionIcon, category: 'Master Data' },
  { title: 'Inventory Brands', href: '/dashboard/masterdata/brands', icon: BusinessIcon, category: 'Master Data' },
  { title: 'Financial Years', href: '/dashboard/masterdata/financial-years', icon: BusinessIcon, category: 'Master Data' },
  { title: 'Import Formats', href: '/dashboard/masterdata/import-formats', icon: DescriptionIcon, category: 'Master Data' },
  { title: 'Business Verticals', href: '/dashboard/masterdata/business-verticals', icon: BusinessIcon, category: 'Master Data' },
  // Quick actions
  { title: 'Create Invoice', href: '/dashboard/business-invoices/create', icon: ReceiptLongIcon, category: 'Actions' },
  { title: 'Create Bill', href: '/dashboard/bills/create', icon: ReceiptIcon, category: 'Actions' },
  { title: 'Create Transaction', href: '/dashboard/transactions/create', icon: SwapHorizIcon, category: 'Actions' },
  { title: 'Create Pay Request', href: '/dashboard/pay-request/create', icon: PaymentsIcon, category: 'Actions' },
  { title: 'Create Client', href: '/dashboard/clients/create', icon: BusinessIcon, category: 'Actions' },
  { title: 'Create Vendor', href: '/dashboard/vendors/create', icon: Storefront, category: 'Actions' },
  { title: 'Create Work Order', href: '/dashboard/work-orders/create', icon: AssignmentIcon, category: 'Actions' },
  { title: 'Create Credit Note', href: '/dashboard/credit-notes/create', icon: AssignmentReturnIcon, category: 'Actions' },
];

export default function GlobalSearch({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return searchItems;
    const q = query.toLowerCase();
    return searchItems.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
    );
  }, [query]);

  const handleSelect = useCallback(
    (item) => {
      navigate(item.href);
      onClose();
    },
    [navigate, onClose]
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filtered[selectedIndex]) {
        e.preventDefault();
        handleSelect(filtered[selectedIndex]);
      }
    },
    [filtered, selectedIndex, handleSelect]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Group items by category
  const grouped = useMemo(() => {
    const groups = {};
    let flatIndex = 0;
    filtered.forEach((item) => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push({ ...item, flatIndex: flatIndex++ });
    });
    return groups;
  }, [filtered]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '15%',
          m: 0,
          borderRadius: 2,
          maxHeight: '60vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <SearchIcon sx={{ color: 'text.disabled', mr: 1 }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search pages, actions..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ fontSize: '1rem' }}
        />
        <Chip
          label="ESC"
          size="small"
          variant="outlined"
          sx={{ ml: 1, fontSize: '0.7rem', height: 24 }}
        />
      </Box>
      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.disabled">
              No results found for "{query}"
            </Typography>
          </Box>
        ) : (
          <List ref={listRef} dense disablePadding>
            {Object.entries(grouped).map(([category, items]) => (
              <Box key={category}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', fontWeight: 600 }}
                >
                  {category}
                </Typography>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <ListItemButton
                      key={item.href}
                      data-selected={item.flatIndex === selectedIndex}
                      selected={item.flatIndex === selectedIndex}
                      onClick={() => handleSelect(item)}
                      sx={{ px: 2, py: 0.75 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Icon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                      />
                    </ListItemButton>
                  );
                })}
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
